/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
import { Map, List, fromJS, Set } from 'immutable';

import composeReducers from 'reducers/composeReducers';
import { getIn, sort } from 'utils';
import {
  GET_REPORT_DATA, GET_REPORTS, SET_REPORT_CONFIG, SET_REPORT_PAGE, SORT_REPORT_DATA,
  SET_REPORT_FILTER_TERM, SET_REPORT_FILTER_DATE, SET_REPORT_DATE_FIELD, SET_REPORT_DATE_PERIOD,
  SET_REPORT_PAGE_SIZE, DOWNLOAD_CSV_FILE, DOWNLOAD_SSV_FILE, CLEAR_DOWNLOAD_HASH,
} from 'actions/reports';


function reportDataResult(reportDataMap, action) {
  const reportItems = action.type === GET_REPORT_DATA.SUCCESS
    ? fromJS(getIn(action, ['payload', 'results'], []))
    : reportDataMap.get('items', List());
  const filterTerms = action.meta.withFilterTerms && action.type === GET_REPORT_DATA.SUCCESS
    ? fromJS(getIn(action, ['payload', 'filters'], Map()))
    : reportDataMap.get('filterTerms', Map());
  return reportDataMap.merge({
    items: reportItems,
    filterTerms,
    filters: action.meta.filters,
    searchString: action.meta.q,
    page: action.meta.page,
    ordering: action.meta.ordering,
    pageSize: action.meta.pageSize,
    additionalParams: action.meta.additionalParams,
    count: getIn(action, ['payload', 'count'], reportDataMap.get('count')),
  });
}

function reportsReducer(state = Map(), action) {
  switch (action.type) {
    case GET_REPORT_DATA.REQUEST:
    case GET_REPORT_DATA.SUCCESS: {
      return state.update(
        action.meta.reportName,
        existing => reportDataResult(existing || Map(), action),
      );
    }
    case GET_REPORTS.SUCCESS:
      return state.merge(fromJS(action.payload));
    case SET_REPORT_PAGE:
      return state.setIn(
        ['pageNumbers', action.payload.id],
        action.payload.page,
      );
    case SORT_REPORT_DATA:
      return state.updateIn(
        [action.payload.reportName, 'items', action.payload.id],
        items => sort(
          items,
          item => item.get(action.payload.path, ''),
          !action.payload.reverse,
        ),
      );
    case SET_REPORT_CONFIG:
      return state.set('config', action.payload);
    case SET_REPORT_FILTER_DATE:
      return state.setIn(
        [action.payload.reportName, 'filters', action.payload.name],
        action.payload.date || '',
      );
    case SET_REPORT_FILTER_TERM: {
      const path = [action.payload.reportName, 'filters', action.payload.filterName];
      const terms = state.getIn(path, Set());
      return state.setIn(
        path,
        action.payload.selected
          ? terms.add(action.payload.term)
          : terms.remove(action.payload.term),
      );
    }
    case SET_REPORT_DATE_FIELD:
      return state.setIn(
        [action.payload.reportName, 'additionalParams', 'datefield'],
        action.payload.field,
      );
    case SET_REPORT_DATE_PERIOD:
      return state.setIn(
        [action.payload.reportName, 'datePeriod'],
        action.payload.period,
      );
    case SET_REPORT_PAGE_SIZE:
      return state.setIn(
        [action.payload.reportName, 'pageSize'],
        action.payload.pageSize,
      );
    case DOWNLOAD_CSV_FILE.SUCCESS:
      return state.setIn(
        [action.meta.reportName, 'csvHashReady'],
        true,
      );
    case DOWNLOAD_SSV_FILE.SUCCESS:
      return state.setIn(
        [action.meta.reportName, 'ssvHashReady'],
        true,
      );
    case CLEAR_DOWNLOAD_HASH:
      return state.deleteIn(
        [action.payload.reportName, 'csvHashReady'],
      ).deleteIn(
        [action.payload.reportName, 'ssvHashReady'],
      );
    default:
      return state;
  }
}

export default composeReducers([
  reportsReducer,
], Map());
